import { useState } from 'react'

import { generateTouchedObject } from '../libs/formik'
import { isNilOrEmpty } from '../utils'

// Enhances Formik with some custom behavior
//
// 1. Reconciliation between Formik and server-side errors.
// 2. More intelligen `touched` generation on submit.
//
// On the topic of form errors (1 above):
//
// - Hides server-side errors on change because they are potentially not
//   relevant anymore if the form has changed.
// - Field errors are hidden when the field itself changes.
// - Global errors are hidden when _any_ field changes.

const useEnhancedFormik = (fieldPropTypes, formik, serverErrors) => {
  const [hiddenErrors, setHiddenErrors] = useState({})

  // These are not wrapped in `useCallback` because Formik does not keep the
  // reference
  const handleChange = e => {
    const { name, id } = e.target

    const field = name && name.length > 0 ? name : id

    setHiddenErrors(hiddenErrors => ({
      ...hiddenErrors,
      [field]: true,
    }))

    formik.handleChange(e)
  }

  const handleSubmit = e => {
    formik.handleSubmit(e)

    // See `generateTouchedObject` documentation for why this is here
    formik.setFormikState(state => ({
      ...state,
      touched: generateTouchedObject(fieldPropTypes, formik.values),
    }))

    setHiddenErrors({})
  }

  const resetForm = nextState => {
    formik.resetForm(nextState)
    setHiddenErrors({})
  }

  const fieldErrors = {
    ...formik.errors,

    ...Object.fromEntries(
      Object.entries(serverErrors?.fieldErrors ?? {}).filter(
        ([k, v]) => !hiddenErrors[k],
      ),
    ),
  }

  const clearGlobalFormErrors = () => {
    setGlobalFormErrors([])
  }

  const [globalFormErrors, setGlobalFormErrors] = useState([])

  const setFormErrors = errors => {
    if (!isNilOrEmpty(errors)) {
      setHiddenErrors({})
      setGlobalFormErrors(errors)
    }
  }

  const formErrors =
    !isNilOrEmpty(hiddenErrors) || !isNilOrEmpty(globalFormErrors)
      ? globalFormErrors
      : !isNilOrEmpty(serverErrors?.errors)
      ? serverErrors.errors
      : []

  return {
    handleChange,
    handleSubmit,
    fieldErrors,
    formErrors,
    resetForm,
    setFormErrors,
    clearGlobalFormErrors,
  }
}

export default useEnhancedFormik
